import { Tag } from 'antd';

/**
 * 场景来源（``scene_inspection.source``）可视化 Tag。
 *
 * 与后端 ``enums.SceneSourceEnum`` 严格对齐：
 * - ``jira`` → Jira 导入（jira / 自动扫描）
 * - ``data_platform`` → 数据中台 / Kafka 监听 / 历史手工创建
 * - ``manual_upload`` → UI 手工上传
 * - ``collect_task`` → 回收任务（按 ``task_id`` 直接建场景）
 * - ``data_mining`` → 数据挖掘（预留入口，目前后端尚无写入路径）
 * - ``case_flow`` → Case-Flow 平台推送（POST /scene-creation/case-flow/scenes）
 *
 * 颜色映射哲学：
 * - jira（外部跟踪系统单据） → blue（外蓝色）
 * - data_platform（数据流水线，最常见） → geekblue（区分但仍属"蓝系"，避免与 Tag 状态色冲突）
 * - manual_upload（人工上传） → cyan（与中台蓝系区分）
 * - collect_task（运营回收，相对低频） → gold（暖色突出但不报警）
 * - data_mining（算法/挖掘产出，独立通道） → purple（与蓝系/暖系都拉开，强信号但非告警）
 * - case_flow（Case-Flow 平台推送，独立通道） → magenta（与其它色系全部拉开）
 *
 * 未识别的 enum 值原样回显为默认色 Tag，便于 enum 漂移时排查，不会变 "-" 失去信号。
 */

export const SCENE_SOURCE_META: Record<string, { label: string; color: string }> = {
  jira: { label: 'Jira', color: 'blue' },
  data_platform: { label: '数据中台', color: 'geekblue' },
  manual_upload: { label: '手动上传', color: 'cyan' },
  collect_task: { label: '回收任务', color: 'gold' },
  data_mining: { label: '数据挖掘', color: 'purple' },
  case_flow: { label: 'Case-Flow', color: 'magenta' },
};

/** 筛选下拉数据源；顺序按业务高频度从上往下，与表格列同源避免漂移。 */
export const SCENE_SOURCE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'data_platform', label: SCENE_SOURCE_META.data_platform.label },
  { value: 'manual_upload', label: SCENE_SOURCE_META.manual_upload.label },
  { value: 'jira', label: SCENE_SOURCE_META.jira.label },
  { value: 'collect_task', label: SCENE_SOURCE_META.collect_task.label },
  { value: 'case_flow', label: SCENE_SOURCE_META.case_flow.label },
  { value: 'data_mining', label: SCENE_SOURCE_META.data_mining.label },
];

interface SceneSourceTagProps {
  source?: string | null;
}

export default function SceneSourceTag({ source }: SceneSourceTagProps) {
  if (source == null || source === '') {
    return <span style={{ color: 'rgba(0,0,0,0.45)' }}>-</span>;
  }
  const key = String(source);
  const meta = SCENE_SOURCE_META[key];
  if (!meta) {
    return <Tag>{key}</Tag>;
  }
  return <Tag color={meta.color}>{meta.label}</Tag>;
}
